import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import { Input } from "@blueskydataplatform/shared-ui/components/ui/input";
import { Label } from "@blueskydataplatform/shared-ui/components/ui/label";
import { UILink } from "@/components/ui-link";
import { paths } from "@/config/paths";
import { toast } from "@blueskydataplatform/shared-ui/hooks/use-toast";
import { useResetPasswordPetition } from "../api/reset-password-petition";
import { passwordResetPetitionSchema } from "../schemas/password-reset-petition-schema";
import Logo from "@/components/logo";

type PasswordResetPetitionFormValues = z.infer<typeof passwordResetPetitionSchema>;

const PasswordResetForm = ({
  onPasswordResetPetitionSuccess,
}: {
  onPasswordResetPetitionSuccess: (email: string) => void;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<PasswordResetPetitionFormValues>({
    resolver: zodResolver(passwordResetPetitionSchema),
    defaultValues: {
      email: "",
    },
  });

  const sendPasswordResetMutation = useResetPasswordPetition({
    mutationConfig: {
      onSuccess: () => {
        onPasswordResetPetitionSuccess(getValues("email"));
      },
      onError: (error: any) => {
        if (error.response.status === 400) {
          const data = error.response.data;
          if (data.error === "TOO_MANY") {
            toast.error("We've sent too many requests to this email address. Please try again later.");
            return;
          }
        }
        toast.error("Failed to send password reset email. Please try again later.");
      },
    },
  });

  const onSubmit = (data: PasswordResetPetitionFormValues) => {
    sendPasswordResetMutation.mutate({ data });
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          {/* Logo placeholder */}
          <div className="flex justify-center mb-2">
            <Logo className="mb-4" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Reset your password</CardTitle>
          <CardDescription className="text-center">
            Enter the email address associated with your account to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="flex flex-col gap-2 mb-3">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  disabled={sendPasswordResetMutation.isPending}
                  {...register("email")}
                  className={errors.email ? "border-red-500" : ""}
                />
                {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <Button type="submit" disabled={sendPasswordResetMutation.isPending} className="w-full">
                {sendPasswordResetMutation.isPending ? "Sending reset link..." : "Send reset link"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <UILink preserveQuery to={paths.home.getHref()} className="text-sm">
                Return to Sign in
              </UILink>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordResetForm;
